/**
 * Thin orchestrator that wires together file-list management,
 * the merge/upload pipeline, and J-code document updates.
 * @module useFileUploadPipeline
 */

import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import {
  AuthOnFile,
  GeneralOrderDetails,
} from "../../../../data-model/medicalPaOrdersModel";
import { DocumentTypesForOrder } from "../../../../enums/documentTypes";
import { resetFileUploadState } from "../../../../redux/slice/fileUploadSlice";
import { closeModal } from "../../../../redux/slice/modalSlice";
import { AppDispatch } from "../../../../redux/store/store";
import { NarLetterModalMetaData } from "../types";
import {
  generateApprovalLetterDocumentTitle,
  resolveSelectedJCodeDetails,
} from "../utils/generateApprovalLetterDocumentTitle";
import { useFileListState } from "./useFileListState";
import { useJCodeDocUpdate } from "./useJCodeDocUpdate";
import { useUploadPipeline } from "./useUploadPipeline";

/** Wiring parameters for `useFileUploadPipeline` (order context, callbacks, abort ref). */
interface UseFileUploadPipelineParams {
  authOnFileDetails: AuthOnFile | null | undefined;
  generalOrderDetails: GeneralOrderDetails | null | undefined;
  metaData: NarLetterModalMetaData | undefined;
  selectedMedicines: string[];
  /** Payer name used to pre-fill the auth-approval letter document title. */
  payerName: string;
  userEmail: string;
  onSuccess?: (id: string, docPath: string, documentTitle: string) => void;
  onAgentRerunStatusChange?: (
    isRunning: boolean,
    jCodeId: string,
    docPath: string,
  ) => void;
  onClose: () => void;
  onDocUploaded: (
    docTitle: string,
    docPath: string,
    isLetterSelectedFromDropdown: boolean,
  ) => void;
  /** Caller-owned ref for aborting in-flight document update requests. */
  abortControllerRef: React.MutableRefObject<AbortController | null>;
}

/**
 * Orchestrates the multi-step file upload pipeline for the NAR letter modal.
 *
 * Delegates to three focused hooks:
 *   - `useFileListState` — local file list CRUD
 *   - `useUploadPipeline` — base64 -> merge -> Firebase upload
 *   - `useJCodeDocUpdate` — Postgres J-code document updates after upload
 *
 * @param params - Auth data, metadata, callbacks, and abort ref.
 * @returns File state, progress indicators, and action handlers.
 */
export const useFileUploadPipeline = ({
  authOnFileDetails,
  generalOrderDetails,
  metaData,
  selectedMedicines,
  payerName,
  userEmail,
  onSuccess,
  onAgentRerunStatusChange,
  onClose,
  onDocUploaded,
  abortControllerRef,
}: UseFileUploadPipelineParams) => {
  const [documentTitle, setDocumentTitle] = useState("");
  const dispatch = useDispatch<AppDispatch>();
  const isLetterSelectedFromDropdownAtSaveRef = useRef(false);
  const autoGeneratedTitleRef = useRef("");

  useEffect(() => {
    if (metaData?.docType !== DocumentTypesForOrder.APPROVAL_LETTER) {
      return;
    }

    const nextTitle = generateApprovalLetterDocumentTitle(
      payerName,
      resolveSelectedJCodeDetails(authOnFileDetails, selectedMedicines),
    );
    if (!nextTitle) {
      return;
    }

    // Only pre-fill when the field is untouched (empty) or still holds a
    // previously auto-generated value, so a manual edit is never clobbered
    // while the title still tracks the current drug selection.
    setDocumentTitle((current) => {
      if (current === "" || current === autoGeneratedTitleRef.current) {
        autoGeneratedTitleRef.current = nextTitle;
        return nextTitle;
      }
      return current;
    });
  }, [metaData?.docType, payerName, authOnFileDetails, selectedMedicines]);

  const {
    fileNames,
    uploadedFiles,
    isLetterSelectedFromDropdown,
    fileInputRef,
    handleFileChange,
    handleFileDelete,
    addFileFromStorage,
  } = useFileListState();

  const {
    isUploading,
    setIsUploading,
    isMerging,
    isMergingRef,
    progress,
    fileUploadPath,
    downloadURL,
    mergedFile,
    handleOnSave: triggerUploadPipeline,
  } = useUploadPipeline({
    orderId: generalOrderDetails?.order_id ?? "",
    uploadedFiles,
    authStatus: metaData?.authStatus ?? "",
    docType: metaData?.docType ?? "",
    onClose,
  });

  useJCodeDocUpdate({
    authOnFileDetails,
    generalOrderDetails,
    selectedMedicines,
    metaData,
    fileUploadPath,
    documentTitle,
    userEmail,
    fileForRerun: mergedFile ?? uploadedFiles[0] ?? null,
    payerName,
    isUploading,
    setIsUploading,
    isMergingRef,
    downloadURL,
    abortControllerRef,
    isLetterSelectedFromDropdownAtSaveRef,
    onDocUploaded,
    onSuccess,
    onAgentRerunStatusChange,
    dispatch,
  });

  const handleOnSave = async () => {
    isLetterSelectedFromDropdownAtSaveRef.current =
      isLetterSelectedFromDropdown;
    await triggerUploadPipeline();
  };

  const handleDismiss = () => {
    dispatch(resetFileUploadState());
    dispatch(closeModal());
    onClose();
  };

  return {
    fileNames,
    uploadedFiles,
    isUploading,
    isMerging,
    documentTitle,
    setDocumentTitle,
    progress,
    fileInputRef,
    handleOnSave,
    handleFileChange,
    handleFileDelete,
    addFileFromStorage,
    handleDismiss,
  };
};
